import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Handshake, Plus, Send, Building2, Mail, CheckCircle2, Clock, Wallet,
  Pause, X, ExternalLink, Receipt, Copy,
} from "lucide-react";
import { toast } from "sonner";
import { relativeTime } from "@/lib/utils";

const TYPE_LABEL = {
  handling_agent:   "🤝 Handling agent",
  network_partner:  "🌐 Network partner",
  exclusive_agent:  "⭐ Exclusive agent",
};

const STATUS_BADGE = {
  active:   "text-emerald-500 border-emerald-500/40 bg-emerald-500/10",
  paused:   "text-amber-accent border-amber-accent/40 bg-amber-accent/10",
  canceled: "text-destructive border-destructive/40 bg-destructive/10",
};

export default function PartnershipsPage() {
  const [params] = useSearchParams();
  const [partnerships, setPartnerships] = useState([]);
  const [stripeOk, setStripeOk] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    partner_company: "", partner_contact_name: "", partner_email: "",
    partnership_type: "exclusive_agent", monthly_retainer_eur: 5000, notes: "",
  });
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/partnerships");
      setPartnerships(data.partnerships || []);
      setStripeOk(Boolean(data.stripe_configured));
    } catch { toast.error("Failed to load partnerships"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll Stripe status if redirected back
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    (async () => {
      try {
        const { data } = await api.get(`/partnerships/checkout-status/${sid}`);
        if (data.status === "paid") toast.success("Payment received");
        else toast.message(`Payment status: ${data.status}`);
        load();
      } catch { /* ignore */ }
    })();
  }, [params, load]);

  const create = async () => {
    if (!form.partner_company.trim() || !form.partner_email.trim()) {
      toast.error("Company + email required"); return;
    }
    setBusy("create");
    try {
      await api.post("/partnerships", form);
      toast.success("Partnership added");
      setCreateOpen(false);
      setForm({ partner_company: "", partner_contact_name: "", partner_email: "", partnership_type: "exclusive_agent", monthly_retainer_eur: 5000, notes: "" });
      load();
    } catch (e) {
      toast.error(`Create failed: ${e?.response?.data?.detail || "unknown"}`);
    }
    setBusy(null);
  };

  const sendInvoice = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await api.post(`/partnerships/${p.id}/send-invoice`, {
        origin_url: window.location.origin,
      });
      // Copy URL to clipboard + show toast
      await navigator.clipboard.writeText(data.payment_url);
      toast.success(`Invoice created (${data.period_label}). Payment URL copied to clipboard.`);
      window.open(data.payment_url, "_blank", "noopener,noreferrer");
      load();
    } catch (e) {
      toast.error(`Invoice failed: ${e?.response?.data?.detail || "unknown"}`);
    }
    setBusy(null);
  };

  const pause = async (p) => {
    try {
      await api.patch(`/partnerships/${p.id}`, { status: p.status === "active" ? "paused" : "active" });
      load();
    } catch (e) { toast.error(`Update failed: ${e?.response?.data?.detail || "unknown"}`); }
  };

  const totalMrr = partnerships
    .filter((p) => p.status === "active")
    .reduce((sum, p) => sum + (p.monthly_retainer_eur || 0), 0);

  return (
    <DashboardLayout
      eyebrow="Recurring revenue"
      title="Partnership Billing"
      actions={
        <Button onClick={() => setCreateOpen(true)} data-testid="pb-create-btn" className="rounded-sm" disabled={!stripeOk}>
          <Plus className="w-4 h-4 mr-1.5" /> New partnership
        </Button>
      }
    >
      {!stripeOk && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-sm p-3 mb-6 text-xs" data-testid="pb-stripe-warning">
          ⚠️ Stripe not configured — set STRIPE_API_KEY in backend env.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="data-card !p-3" data-testid="pb-mrr">
          <div className="label-eyebrow inline-flex items-center gap-2"><Wallet className="w-3.5 h-3.5" /> Active MRR</div>
          <div className="font-mono text-2xl font-semibold text-emerald-500">€{totalMrr.toLocaleString("en-US")}</div>
          <div className="text-[0.65rem] text-muted-foreground">€{(totalMrr * 12).toLocaleString("en-US")} / year</div>
        </div>
        <div className="data-card !p-3">
          <div className="label-eyebrow inline-flex items-center gap-2"><Handshake className="w-3.5 h-3.5" /> Active partners</div>
          <div className="font-mono text-2xl font-semibold">{partnerships.filter((p) => p.status === "active").length}</div>
          <div className="text-[0.65rem] text-muted-foreground">of {partnerships.length} total</div>
        </div>
        <div className="data-card !p-3">
          <div className="label-eyebrow inline-flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> Due in 7 days</div>
          <div className="font-mono text-2xl font-semibold">
            {partnerships.filter((p) => {
              if (p.status !== "active" || !p.next_invoice_date) return false;
              const days = (new Date(p.next_invoice_date) - new Date()) / 86400000;
              return days <= 7;
            }).length}
          </div>
          <div className="text-[0.65rem] text-muted-foreground">Invoice these soon</div>
        </div>
      </div>

      {partnerships.length === 0 ? (
        <div className="data-card text-center py-16" data-testid="pb-empty">
          <Handshake className="w-10 h-10 text-signal-500 mx-auto mb-3" />
          <h3 className="font-display text-lg font-semibold mb-1">No partnerships yet</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
            The moment you sign an exclusive-agent / handling-agent agreement with Logwin, MBS, Hellmann, etc. → add them here and invoice them monthly in 2 clicks.
          </p>
          <Button onClick={() => setCreateOpen(true)} disabled={!stripeOk} data-testid="pb-empty-create" className="rounded-sm">
            <Plus className="w-4 h-4 mr-1.5" /> Add first partnership
          </Button>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="pb-list">
          {partnerships.map((p) => (
            <li key={p.id} className="data-card" data-testid={`pb-row-${p.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold text-base inline-flex items-center gap-2 flex-wrap">
                    {p.partner_company}
                    <span className={`px-1.5 py-0.5 rounded-sm text-[0.6rem] tracking-widest uppercase font-mono border ${STATUS_BADGE[p.status] || ""}`}>
                      {p.status}
                    </span>
                    <span className="text-[0.65rem] font-mono text-muted-foreground">{TYPE_LABEL[p.partnership_type] || p.partnership_type}</span>
                  </div>
                  <div className="text-xs text-muted-foreground inline-flex items-center gap-3 mt-0.5">
                    {p.partner_contact_name && <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> {p.partner_contact_name}</span>}
                    <a href={`mailto:${p.partner_email}`} className="inline-flex items-center gap-1 hover:text-signal-500">
                      <Mail className="w-3 h-3" /> {p.partner_email}
                    </a>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-lg font-semibold text-emerald-500">€{(p.monthly_retainer_eur || 0).toLocaleString("en-US")}</div>
                  <div className="text-[0.6rem] text-muted-foreground">per month</div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[0.65rem] text-muted-foreground mb-3">
                <div>Started <span className="text-foreground">{p.start_date ? relativeTime(p.start_date) : "—"}</span></div>
                <div>Next invoice <span className={p.next_invoice_date && new Date(p.next_invoice_date) < new Date() ? "text-amber-accent font-semibold" : "text-foreground"}>{p.next_invoice_date ? relativeTime(p.next_invoice_date) : "—"}</span></div>
                <div>Last paid <span className="text-foreground">{p.last_paid_at ? relativeTime(p.last_paid_at) : "—"}</span></div>
                <div>Annual run-rate <span className="text-foreground">€{((p.monthly_retainer_eur || 0) * 12).toLocaleString("en-US")}</span></div>
              </div>

              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  onClick={() => sendInvoice(p)}
                  disabled={busy === p.id || p.status !== "active"}
                  data-testid={`pb-invoice-${p.id}`}
                  className="rounded-sm"
                >
                  <Receipt className="w-3.5 h-3.5 mr-1.5" />
                  {busy === p.id ? "Creating…" : "Generate invoice link"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => pause(p)}
                  disabled={p.status === "canceled"}
                  data-testid={`pb-pause-${p.id}`}
                  className="rounded-sm"
                >
                  {p.status === "active" ? <><Pause className="w-3.5 h-3.5 mr-1.5" /> Pause</> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Resume</>}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {createOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => !busy && setCreateOpen(false)} data-testid="pb-create-modal">
          <div className="bg-card border border-border rounded-sm w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <div className="font-display font-semibold inline-flex items-center gap-2"><Plus className="w-4 h-4" /> New partnership</div>
              <button onClick={() => setCreateOpen(false)} data-testid="pb-modal-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <Label className="text-xs mb-1 block">Partner company</Label>
                <Input data-testid="pb-form-company" value={form.partner_company} onChange={(e) => setForm({ ...form, partner_company: e.target.value })} placeholder="e.g. MBS Logistics" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs mb-1 block">Contact name</Label>
                  <Input data-testid="pb-form-contact" value={form.partner_contact_name} onChange={(e) => setForm({ ...form, partner_contact_name: e.target.value })} placeholder="Hans Müller" />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Contact email</Label>
                  <Input data-testid="pb-form-email" type="email" value={form.partner_email} onChange={(e) => setForm({ ...form, partner_email: e.target.value })} placeholder="hans@mbs.de" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs mb-1 block">Type</Label>
                  <select
                    data-testid="pb-form-type"
                    value={form.partnership_type}
                    onChange={(e) => setForm({ ...form, partnership_type: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-sm"
                  >
                    <option value="handling_agent">Handling agent</option>
                    <option value="network_partner">Network partner</option>
                    <option value="exclusive_agent">Exclusive agent</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Monthly retainer (€)</Label>
                  <Input
                    data-testid="pb-form-amount"
                    type="number" min="1" max="100000" step="100"
                    value={form.monthly_retainer_eur}
                    onChange={(e) => setForm({ ...form, monthly_retainer_eur: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Notes (optional)</Label>
                <textarea
                  data-testid="pb-form-notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  placeholder="Scope, lanes, exclusivity terms, kick-off date…"
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-sm"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={busy === "create"}>Cancel</Button>
                <Button onClick={create} disabled={busy === "create"} data-testid="pb-form-submit">
                  {busy === "create" ? "Adding…" : "Add partnership"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
